/**
 * Data terms — the legal basis for training on ORG requirement data (owner decision #2).
 *
 * The flywheel logging (taxonomy_inferences, question_plan_snapshots, taxonomy_suggestions —
 * migration 0045) records what orgs write and correct; these terms are what gives us the RIGHT
 * to learn from it. An org admin accepts a VERSION of the terms; the acceptance is an
 * append-only row in org_data_terms_acceptances (migration 0049), and training extraction is
 * gated on the CURRENT version having been accepted (apps/api/src/utils/orgDataTerms.ts).
 *
 * The SECTIONS below are the canonical machine-readable copy: the recruiter-facing acceptance
 * modal renders them verbatim, and dataTerms.test.ts asserts docs/DATA_TERMS.md (the human
 * document) carries the same version, date and section headings — so the text a customer
 * accepted in the app can never silently drift from the document we would show a lawyer.
 *
 * Candidate personal data is deliberately OUT of these terms: it is governed by each
 * candidate's own consent + erasure rights (consent ledger, DSR flow), a separate regime these
 * terms must never be read as extending.
 */
import { z } from "zod";
/**
 * The version an org must have accepted before its data may feed training. Bump ONLY alongside
 * a real change to the terms (new v-row in docs/DATA_TERMS.md): every bump un-accepts every org
 * until an admin accepts again, which is exactly the intended behaviour — acceptance of one
 * version never silently extends to the next.
 */
export const DATA_TERMS_VERSION = "v1";
/** Date the current version came into force — printed beside the version wherever it shows. */
export const DATA_TERMS_EFFECTIVE_DATE = "2026-07-30";
/**
 * The terms, section by section, exactly as the acceptance modal shows them. Headings must
 * match docs/DATA_TERMS.md (asserted by the unit test).
 */
export const DATA_TERMS_SECTIONS = [
    {
        heading: "What we collect from your organisation",
        bullets: [
            "Requirement text — the job descriptions and requirement descriptions your team writes or pastes when raising a request.",
            "Taxonomy selections and corrections — the industry, role family and specialisation your team picks, including where you correct what our matching suggested.",
            "Question-plan edits — the interview question plans we generate and the edits your team makes to them before inviting candidates.",
            "Gap answers — the clarifying answers your team gives when a requirement is missing information we need.",
        ],
    },
    {
        heading: "What we use it for",
        bullets: [
            "Improving matching — your selections and corrections teach the system to map requirement text to the right industry, role and specialisation.",
            "Improving question generation — your edits to generated question plans teach the system what a good plan for a requirement like yours looks like.",
            "Training our own models — we may use the data above to train and evaluate models we build and operate ourselves, for the two purposes above.",
        ],
    },
    {
        heading: "What we never do",
        bullets: [
            "Candidate personal data is out of scope — it is governed separately by each candidate's own consent and erasure rights, and is not used under these terms.",
            "Your data is never shown to other organisations — nothing your organisation writes is ever surfaced to any other customer.",
            "No sale of your data — we do not sell or license your organisation's data to anyone.",
        ],
    },
    {
        heading: "Opting out",
        bullets: [
            "Write to privacy@talenthopai.com from an admin account and we will stop using your organisation's data for service improvement and model training. Opting out does not affect your use of the product.",
        ],
    },
    {
        heading: "Changes to these terms",
        bullets: [
            "These terms are versioned and dated. A future version that changes what we collect or how we use it must be accepted afresh before your data is used under it.",
        ],
    },
];
/** One acceptance row — append-only history, mirrors org_data_terms_acceptances (0049). */
export const orgDataTermsAcceptanceSchema = z.object({
    orgId: z.string().uuid(),
    version: z.string().regex(/^v\d+$/),
    acceptedByUserId: z.string().uuid(),
    acceptedAt: z.string().datetime(),
});
//# sourceMappingURL=dataTerms.js.map